import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Plus, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { bankAPI, mandantenAPI } from '../lib/api';
import BankKontoFormModal from '../components/BankKontoFormModal';
import BewegungMatchModal from '../components/BewegungMatchModal';

function fmtMoney(v) {
  const n = Number(v || 0);
  const isNeg = n < 0;
  const fmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Math.abs(n));
  return <span style={{ color: isNeg ? '#991b1b' : '#166534' }}>{isNeg ? '-' : '+'}{fmt}</span>;
}

function fmtDate(s) {
  if (!s) return '—';
  return format(new Date(s), 'dd.MM.yyyy');
}

const STATUS_LABELS = {
  offen: 'Offen',
  verbucht: 'Verbucht',
  ignoriert: 'Ignoriert',
};

export default function Bank() {
  const { slug } = useParams();
  const fileInputRef = useRef(null);
  const [mandant, setMandant] = useState(null);
  const [konten, setKonten] = useState([]);
  const [selectedKontoId, setSelectedKontoId] = useState('');
  const [statusF, setStatusF] = useState('offen');
  const [bewegungen, setBewegungen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kontoFormOpen, setKontoFormOpen] = useState(false);
  const [matchBewegung, setMatchBewegung] = useState(null);

  useEffect(() => {
    mandantenAPI.get(slug).then((r) => setMandant(r.data)).catch(() => {});
  }, [slug]);

  const reloadKonten = useCallback(() => {
    return bankAPI.listKonten(slug).then((r) => setKonten(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => { reloadKonten(); }, [reloadKonten]);

  const reloadBewegungen = useCallback(() => {
    setLoading(true);
    return bankAPI
      .listBewegungen(slug, {
        bank_konto_id: selectedKontoId || undefined,
        status: statusF || undefined,
      })
      .then((r) => setBewegungen(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }, [slug, selectedKontoId, statusF]);

  useEffect(() => { reloadBewegungen(); }, [reloadBewegungen]);

  const onPickFile = () => {
    if (konten.length === 0) {
      toast.error('Erst Bank-Konto anlegen');
      return;
    }
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const toastId = toast.loading(`Importiere ${file.name}…`);
    try {
      const r = await bankAPI.upload(slug, file, selectedKontoId || null);
      const { bewegungen_importiert, bewegungen_duplikate, bewegungen_im_file, bank_konto_iban } = r.data;
      toast.success(
        `${bank_konto_iban}: ${bewegungen_importiert} neu, ${bewegungen_duplikate} Duplikate (von ${bewegungen_im_file})`,
        { id: toastId, duration: 5000 }
      );
      reloadBewegungen();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import fehlgeschlagen', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Bank</h1>
          {mandant && (
            <div className="muted">{mandant.name} · {mandant.skr_variant}</div>
          )}
        </div>
        <div className="header-meta" style={{ gap: 8, display: 'flex' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml,.camt,.camt053"
            style={{ display: 'none' }}
            onChange={onFileChosen}
          />
          <button className="btn-secondary" onClick={onPickFile} disabled={uploading}>
            <Upload size={14} /> {uploading ? 'Importiert…' : 'CAMT.053 hochladen'}
          </button>
          <button className="btn-primary" onClick={() => setKontoFormOpen(true)}>
            <Plus size={14} /> Bank-Konto
          </button>
        </div>
      </header>

      {konten.length === 0 ? (
        <div className="card empty" style={{ marginBottom: 16 }}>
          Noch keine Bank-Konten — leg erst eines an (IBAN + Sachkonto-Mapping), dann kann ein CAMT.053-Auszug importiert werden.
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {konten.map((k) => (
              <div key={k.id} style={{
                padding: '8px 12px',
                background: String(selectedKontoId) === String(k.id) ? '#dbeafe' : '#f1f5f9',
                borderRadius: 6, cursor: 'pointer', fontSize: 13,
              }} onClick={() => setSelectedKontoId(String(selectedKontoId) === String(k.id) ? '' : String(k.id))}>
                <div><b>{k.name}</b> <span className="muted small">Konto {k.sachkonto}</span></div>
                <div className="muted small">{k.iban}</div>
              </div>
            ))}
            {selectedKontoId && (
              <button className="btn-text" onClick={() => setSelectedKontoId('')}>Filter aufheben</button>
            )}
          </div>
        </div>
      )}

      <div className="filters">
        <label>
          Status:
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)}>
            <option value="">Alle</option>
            <option value="offen">Offen</option>
            <option value="verbucht">Verbucht</option>
            <option value="ignoriert">Ignoriert</option>
          </select>
        </label>
        <button className="btn-text" onClick={reloadBewegungen} disabled={loading}>
          {loading ? 'Lädt…' : 'Neu laden'}
        </button>
      </div>

      {loading ? (
        <div className="muted">Lädt…</div>
      ) : bewegungen.length === 0 ? (
        <div className="card empty">Keine Bewegungen.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Konto</th>
              <th>Gegenseite</th>
              <th>Verwendungszweck</th>
              <th className="num">Betrag</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {bewegungen.map((b) => (
              <tr key={b.id}>
                <td>{fmtDate(b.buchungs_datum)}</td>
                <td className="muted small">{b.bank_konto_name}</td>
                <td>
                  {b.gegenseite_name || '—'}
                  {b.gegenseite_iban && (
                    <div className="muted small">{b.gegenseite_iban}</div>
                  )}
                </td>
                <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.verwendungszweck || '—'}
                </td>
                <td className="num">{fmtMoney(b.betrag)}</td>
                <td>
                  <span className={`badge badge-${b.status === 'verbucht' ? 'gebucht' : (b.status === 'ignoriert' ? 'verworfen' : 'eingang')}`}>
                    {STATUS_LABELS[b.status]}
                  </span>
                </td>
                <td>
                  {b.status === 'offen' && (
                    <button className="btn-text" onClick={() => setMatchBewegung(b)}>
                      Zuordnen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <BankKontoFormModal
        open={kontoFormOpen}
        onClose={() => setKontoFormOpen(false)}
        slug={slug}
        skrVariant={mandant?.skr_variant || 'SKR03'}
        onSaved={reloadKonten}
      />

      <BewegungMatchModal
        open={!!matchBewegung}
        onClose={() => setMatchBewegung(null)}
        slug={slug}
        bewegung={matchBewegung}
        onMatched={() => { setMatchBewegung(null); reloadBewegungen(); }}
      />
    </div>
  );
}
